import { ApolloClient, useApolloClient } from "@apollo/client";
import { useMemo, useRef } from "react";

import { SHIPPING_PAPERWORK_QUERY } from "./api";
import { BILL_OF_LADING_ROW_STATUS } from "./billOfLadingApi";
import { PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY } from "./useShowFilterCounts";

export const ROW_POLL_INTERVAL_MS = 5000;
// 2 minutes: process-manifests ran for about 45s on gert after the reading.
export const ROW_POLL_MAX_ATTEMPTS = 24;

type RowClient = Pick<ApolloClient<object>, "query" | "refetchQueries">;

/** Refetching a query nothing is subscribed to is a no-op, so any host page is safe. */
export function refetchShipmentDocumentsLists(client: Pick<RowClient, "refetchQueries">) {
  return client.refetchQueries({
    include: [SHIPPING_PAPERWORK_QUERY, PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY],
  });
}

/**
 * Refetches the Shipment Documents grid and counts now, then every 5 seconds
 * until the document has a ProcessedManifest. Gives up after 2 minutes, or as
 * soon as the reading wrote no WasteManifest (it never will have a row).
 * Returns a function that stops it.
 */
export function watchForBillOfLadingRow(client: RowClient, documentId: string): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let attempts = 0;

  const tick = async () => {
    attempts += 1;
    let processedManifestId: string | null | undefined;
    let hasWasteManifest = false;
    try {
      const { data } = await client.query({
        query: BILL_OF_LADING_ROW_STATUS,
        variables: { id: documentId },
        fetchPolicy: "network-only",
      });
      hasWasteManifest = !!data?.document?.sourceWasteManifest;
      processedManifestId = data?.document?.sourceWasteManifest?.processedManifestId;
    } catch {
      return;
    }
    if (cancelled || !hasWasteManifest) return;
    if (processedManifestId) {
      await refetchShipmentDocumentsLists(client);
      return;
    }
    if (attempts >= ROW_POLL_MAX_ATTEMPTS) return;
    timer = setTimeout(tick, ROW_POLL_INTERVAL_MS);
  };

  void refetchShipmentDocumentsLists(client);
  void tick();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}

/**
 * Starts and cancels the row watch for the document an upload created. Not
 * tied to unmount: closing the upload dialogs must not stop the grid from
 * picking up the row.
 */
export function useBillOfLadingRowRefresh() {
  const client = useApolloClient();
  const cancelRef = useRef<(() => void) | null>(null);
  const watchedRef = useRef<string | null>(null);

  return useMemo(
    () => ({
      // The analyzing dialog can report completion more than once; one watch
      // per document.
      watch: (documentId: string) => {
        if (watchedRef.current === documentId) return;
        watchedRef.current = documentId;
        cancelRef.current?.();
        cancelRef.current = watchForBillOfLadingRow(client, documentId);
      },
      cancel: () => {
        cancelRef.current?.();
        cancelRef.current = null;
        watchedRef.current = null;
      },
    }),
    [client]
  );
}
